import Phaser from 'phaser';
import { EntityManager, Monster, MonsterType, Boss, BossType } from '../entities';
import { PathingSystem } from './PathingSystem';
import { LevelLoader, WaveDef } from './LevelLoader';

// ============================================
// Wave Spawner System - With Boss Support
// Based on tower_leveling_and_bosses.md
// ============================================

export type WaveState = 'idle' | 'starting' | 'spawning' | 'waiting' | 'complete' | 'victory' | 'defeat';

/** Minimum spawn interval (seconds) per monster type so sprites don't overlap on path. */
const MIN_SPAWN_INTERVAL_BY_TYPE: Record<MonsterType, number> = {
  swarm: 0.2,      // size 8, very small
  bat: 0.35,
  runner: 0.35,    // size 14
  crawler: 0.5,    // size 18
  flyer: 0.5,      // size 20
  phased: 0.55,    // size 20
  shielded: 0.7,   // size 24
  brute: 1.5,      // size 35
  juggernaut: 2.0, // size 45
};

interface SpawnTask {
  monsterType: MonsterType;
  pathId: string;
  routeId?: string;
  count: number;
  interval: number;  // seconds between spawns
  delay: number;     // initial delay before first spawn
  spawned: number;
  timer: number;
  delayTimer: number;
}

interface BossTask {
  bossType: BossType;
  pathId: string;
  routeId?: string;
  delay: number;
  spawned: boolean;
  delayTimer: number;
  hpOverride?: number;
  speedMultiplier?: number;
}

interface WaveStats {
  waveNumber: number;
  totalMonsters: number;
  spawnedCount: number;
  killedCount: number;
  leakedCount: number;  // Reached base
  hasBoss: boolean;
  bossKilled: boolean;
}

class WaveSpawnerManager {
  private scene: Phaser.Scene | null = null;
  private scenarioId: string = '';

  // State machine
  private state: WaveState = 'idle';
  private currentWave: number = 0;
  private totalWaves: number = 0;

  // Current wave tasks
  private spawnTasks: SpawnTask[] = [];
  private bossTask: BossTask | null = null;
  private waveStats: WaveStats = this.createEmptyStats(0);

  // Active boss tracking
  private activeBoss: Boss | null = null;

  // Timing
  private waveStartDelay: number = 2; // seconds before wave starts
  private waveEndDelay: number = 2;   // seconds after wave ends before next
  private stateTimer: number = 0;
  private waveSpawnStartTime: number = 0;  // When current wave's spawning started
  private estimatedWaveDuration: number = 30; // Estimated seconds for wave to complete

  /** Spawn interval multiplier (1.5 = 50% farther apart). */
  private spawnIntervalMultiplier: number = 1.5;

  /** Monster count multiplier: 1 + (playerCount - 1) * 0.3 (e.g. 2P = 1.3x, 4P = 1.9x). */
  private monsterCountMultiplier: number = 1;

  // Auto-start waves
  private autoStart: boolean = true;

  // Callbacks
  public onWaveStart?: (waveNumber: number, totalWaves: number) => void;
  public onWaveComplete?: (waveNumber: number, stats: WaveStats) => void;
  public onVictory?: () => void;
  public onDefeat?: () => void;
  public onMonsterSpawned?: (monster: Monster) => void;
  public onMonsterKilled?: (monster: Monster, reward: number) => void;
  public onMonsterReachedBase?: (monster: Monster, damage: number) => void;
  public onBossSpawned?: (boss: Boss) => void;
  public onBossKilled?: (boss: Boss) => void;
  public onBossReachedBase?: (boss: Boss) => void;
  public onBossEatVillager?: (damage: number) => void;

  init(scene: Phaser.Scene, scenarioId: string): void {
    this.scene = scene;
    this.scenarioId = scenarioId;

    // Get wave count from level loader
    this.totalWaves = LevelLoader.getWaveCount(scenarioId);
    this.currentWave = 0;
    this.state = 'idle';
    this.spawnTasks = [];
    this.bossTask = null;
    this.activeBoss = null;
    this.waveStats = this.createEmptyStats(0);

    console.log(`[WaveSpawner] Initialized for ${scenarioId} with ${this.totalWaves} waves`);
  }

  private createEmptyStats(waveNumber: number): WaveStats {
    return {
      waveNumber,
      totalMonsters: 0,
      spawnedCount: 0,
      killedCount: 0,
      leakedCount: 0,
      hasBoss: false,
      bossKilled: false,
    };
  }

  // ============================================
  // State Machine
  // ============================================

  update(delta: number): void {
    if (!this.scene) return;

    const deltaSeconds = delta / 1000;

    switch (this.state) {
      case 'idle':
        // Waiting to start
        if (this.autoStart) {
          this.stateTimer += deltaSeconds;
          if (this.stateTimer >= this.waveStartDelay) {
            this.startNextWave();
          }
        }
        break;

      case 'starting':
        // Brief pause before spawning
        this.stateTimer += deltaSeconds;
        if (this.stateTimer >= 0.5) {
          this.state = 'spawning';
          this.stateTimer = 0;
          this.waveSpawnStartTime = Date.now();
          // Estimate wave duration based on spawn tasks
          this.estimatedWaveDuration = this.calculateWaveDuration();
        }
        break;

      case 'spawning':
        // Actively spawning monsters
        this.updateSpawning(deltaSeconds);
        this.updateBossSpawning(deltaSeconds);

        // Check if all spawning is done
        if (this.allSpawnsComplete()) {
          this.state = 'waiting';
        }
        break;

      case 'waiting':
        // Waiting for all monsters to be resolved
        this.checkWaveCompletion();
        break;

      case 'complete':
        // Wave finished, prepare for next
        this.stateTimer += deltaSeconds;
        if (this.stateTimer >= this.waveEndDelay) {
          if (this.currentWave >= this.totalWaves) {
            this.triggerVictory();
          } else if (this.autoStart) {
            this.startNextWave();
          } else {
            this.state = 'idle';
            this.stateTimer = 0;
          }
        }
        break;

      case 'victory':
      case 'defeat':
        // Game over states - no updates needed
        break;
    }
  }

  private updateSpawning(deltaSeconds: number): void {
    for (const task of this.spawnTasks) {
      if (task.spawned >= task.count) continue;

      // Handle initial delay
      if (task.delayTimer < task.delay) {
        task.delayTimer += deltaSeconds;
        continue;
      }

      // Update spawn timer
      task.timer += deltaSeconds;

      // Spawn if interval elapsed
      while (task.timer >= task.interval && task.spawned < task.count) {
        this.spawnMonster(task);
        task.spawned++;
        task.timer -= task.interval;
      }
    }
  }

  private updateBossSpawning(deltaSeconds: number): void {
    if (!this.bossTask || this.bossTask.spawned) return;

    // Handle boss delay
    this.bossTask.delayTimer += deltaSeconds;
    if (this.bossTask.delayTimer >= this.bossTask.delay) {
      const spawned = this.spawnBoss(this.bossTask);
      if (spawned) this.bossTask.spawned = true;
    }
  }

  private spawnMonster(task: SpawnTask): void {
    if (!this.scene) return;

    // Select route for this monster
    const routeId = task.routeId || PathingSystem.selectRoute(task.pathId);
    const startPos = PathingSystem.getPathStart(task.pathId, routeId);

    if (!startPos) {
      console.error(`[WaveSpawner] Path not found: ${task.pathId}`);
      return;
    }

    const monster = new Monster(this.scene, {
      id: `monster-${this.currentWave}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      x: startPos.x,
      y: startPos.y,
      monsterType: task.monsterType,
      pathId: task.pathId,
      routeId,
    });

    // Set up callbacks
    monster.onReachedBase = () => {
      this.handleMonsterReachedBase(monster);
    };

    monster.onDeath = (reward: number) => {
      this.handleMonsterKilled(monster, reward);
    };

    EntityManager.add(monster);
    this.waveStats.spawnedCount++;
    this.onMonsterSpawned?.(monster);
  }

  private spawnBoss(task: BossTask): boolean {
    if (!this.scene) return false;

    // Use selectRoute when path has multiple routes (e.g. 'left'/'right'); default 'main' is only for single-route paths
    const routeId = task.routeId || PathingSystem.selectRoute(task.pathId);
    const startPos = PathingSystem.getPathStart(task.pathId, routeId);

    if (!startPos) {
      console.error(`[WaveSpawner] Path/route not found for boss: ${task.pathId}, routeId: ${routeId}`);
      return false;
    }

    const boss = new Boss(this.scene, {
      id: `boss-${this.currentWave}-${Date.now()}`,
      x: startPos.x,
      y: startPos.y,
      bossType: task.bossType,
      pathId: task.pathId,
      routeId,
      hpOverride: task.hpOverride,
      speedMultiplier: task.speedMultiplier,
    });

    // Set up boss callbacks
    boss.onReachedBase = () => {
      this.handleBossReachedBase(boss);
    };

    boss.onDeath = () => {
      this.handleBossKilled(boss);
    };

    boss.onEatVillager = (damage: number) => {
      this.onBossEatVillager?.(damage);
    };

    EntityManager.add(boss);
    this.activeBoss = boss;
    this.waveStats.hasBoss = true;
    this.onBossSpawned?.(boss);

    console.log(`[WaveSpawner] Boss spawned: ${task.bossType}`);
    return true;
  }

  private handleMonsterKilled(monster: Monster, reward: number): void {
    this.waveStats.killedCount++;
    this.onMonsterKilled?.(monster, reward);
  }

  private handleMonsterReachedBase(monster: Monster): void {
    this.waveStats.leakedCount++;
    this.onMonsterReachedBase?.(monster, monster.getDamage());
  }

  private handleBossKilled(boss: Boss): void {
    console.log(`[WaveSpawner] Boss killed: ${boss.bossType}`);
    this.waveStats.bossKilled = true;
    this.activeBoss = null;
    this.onBossKilled?.(boss);
  }

  private handleBossReachedBase(boss: Boss): void {
    console.log(`[WaveSpawner] Boss reached base: ${boss.bossType}`);
    this.onBossReachedBase?.(boss);
    // Boss doesn't count as "leaked" - it eats villagers over time instead
  }

  private allSpawnsComplete(): boolean {
    const monstersComplete = this.spawnTasks.every(task => task.spawned >= task.count);
    const bossComplete = !this.bossTask || this.bossTask.spawned;
    return monstersComplete && bossComplete;
  }

  private checkWaveCompletion(): void {
    // Wave is complete when all monsters are spawned AND all are resolved
    const resolved = this.waveStats.killedCount + this.waveStats.leakedCount;
    const remaining = this.waveStats.spawnedCount - resolved;

    // Also check for any active monsters in EntityManager
    const activeMonsters = EntityManager.getCount('monster');

    // Boss must also be dead (not just at base)
    const bossResolved = !this.waveStats.hasBoss || this.waveStats.bossKilled;

    if (remaining <= 0 && activeMonsters === 0 && bossResolved) {
      this.completeWave();
    }
  }

  private completeWave(): void {
    console.log(`[WaveSpawner] Wave ${this.currentWave} complete - Killed: ${this.waveStats.killedCount}, Leaked: ${this.waveStats.leakedCount}`);

    this.state = 'complete';
    this.stateTimer = 0;
    this.activeBoss = null;
    this.onWaveComplete?.(this.currentWave, { ...this.waveStats });
  }

  // ============================================
  // Wave Control
  // ============================================

  startNextWave(): void {
    if (this.state === 'victory' || this.state === 'defeat') return;
    if (this.currentWave >= this.totalWaves) {
      this.triggerVictory();
      return;
    }

    this.currentWave++;
    this.state = 'starting';
    this.stateTimer = 0;

    // Load wave data
    const waveData = LevelLoader.getWaveData(this.scenarioId, this.currentWave);
    if (!waveData) {
      console.error(`[WaveSpawner] No wave data for wave ${this.currentWave}`);
      return;
    }

    // Create spawn tasks
    this.spawnTasks = this.createSpawnTasks(waveData);
    this.bossTask = this.createBossTask(waveData);
    this.waveStats = this.createEmptyStats(this.currentWave);

    // Calculate total monsters
    this.waveStats.totalMonsters = this.spawnTasks.reduce((sum, task) => sum + task.count, 0);

    const bossInfo = this.bossTask ? ` + BOSS (${this.bossTask.bossType})` : '';
    console.log(`[WaveSpawner] Starting wave ${this.currentWave}/${this.totalWaves} - ${this.waveStats.totalMonsters} monsters${bossInfo}`);
    this.onWaveStart?.(this.currentWave, this.totalWaves);
  }

  /** Base minimum interval (seconds) for large monsters before multiplier */
  private static readonly LARGE_MONSTER_BASE_MIN = 1.7;

  private createSpawnTasks(waveData: WaveDef): SpawnTask[] {
    const mult = this.spawnIntervalMultiplier;
    const countMult = this.monsterCountMultiplier;
    return waveData.spawns.map(spawn => {
      const type = spawn.monsterType as MonsterType;
      const baseInterval =
        type === 'brute' || type === 'shielded' || type === 'juggernaut'
          ? Math.max(WaveSpawnerManager.LARGE_MONSTER_BASE_MIN, spawn.interval)
          : spawn.interval;
      const computed = baseInterval * mult;
      const minInterval = MIN_SPAWN_INTERVAL_BY_TYPE[type] ?? 0.35;
      const interval = Math.max(computed, minInterval);
      const count = Math.max(1, Math.round(spawn.count * countMult));
      return {
        monsterType: type,
        pathId: spawn.pathId,
        routeId: spawn.routeId,
        count,
        interval,
        delay: spawn.delay ?? 0,
        spawned: 0,
        timer: interval,
        delayTimer: 0,
      };
    });
  }

  private createBossTask(waveData: WaveDef): BossTask | null {
    if (!waveData.boss) return null;

    return {
      bossType: waveData.boss.type as BossType,
      pathId: waveData.boss.pathId,
      routeId: waveData.boss.routeId,
      delay: waveData.boss.delay ?? 0,
      spawned: false,
      delayTimer: 0,
      hpOverride: waveData.boss.hpOverride,
      speedMultiplier: waveData.boss.speedMultiplier,
    };
  }

  /**
   * Manually start the next wave (if autoStart is false)
   */
  manualStartWave(): void {
    if (this.state === 'idle' || this.state === 'complete') {
      this.startNextWave();
    }
  }

  /**
   * Force start the next wave immediately, even mid-wave
   * Allows stacking multiple waves at once
   */
  forceNextWave(): boolean {
    if (this.state === 'victory' || this.state === 'defeat') return false;
    if (this.currentWave >= this.totalWaves) return false;

    // If we're idle or complete, just start normally
    if (this.state === 'idle' || this.state === 'complete') {
      this.startNextWave();
      return true;
    }

    // Mid-wave: add new wave's spawns to current spawns
    const nextWaveNumber = this.currentWave + 1;
    if (nextWaveNumber > this.totalWaves) return false;

    const waveData = LevelLoader.getWaveData(this.scenarioId, nextWaveNumber);
    if (!waveData) return false;

    // Create new spawn tasks and add to existing
    const newTasks = this.createSpawnTasks(waveData);
    this.spawnTasks.push(...newTasks);

    // Handle boss for the new wave
    const newBossTask = this.createBossTask(waveData);
    if (newBossTask && !this.bossTask) {
      this.bossTask = newBossTask;
    }

    // Update stats
    const additionalMonsters = newTasks.reduce((sum, task) => sum + task.count, 0);
    this.waveStats.totalMonsters += additionalMonsters;

    // Advance wave counter
    this.currentWave = nextWaveNumber;

    // Make sure we're in spawning state
    if (this.state === 'waiting') {
      this.state = 'spawning';
    }

    const bossInfo = newBossTask ? ` + BOSS (${newBossTask.bossType})` : '';
    console.log(`[WaveSpawner] FORCE starting wave ${this.currentWave}/${this.totalWaves} - ${additionalMonsters} monsters${bossInfo}`);
    this.onWaveStart?.(this.currentWave, this.totalWaves);

    return true;
  }

  /**
   * Trigger defeat (called when villagers reach 0)
   */
  triggerDefeat(): void {
    if (this.state === 'defeat' || this.state === 'victory') return;

    console.log('[WaveSpawner] DEFEAT');
    this.state = 'defeat';
    this.onDefeat?.();
  }

  private triggerVictory(): void {
    if (this.state === 'victory' || this.state === 'defeat') return;

    console.log('[WaveSpawner] VICTORY');
    this.state = 'victory';
    this.onVictory?.();
  }

  // ============================================
  // Getters
  // ============================================

  getState(): WaveState {
    return this.state;
  }

  getCurrentWave(): number {
    return this.currentWave;
  }

  getTotalWaves(): number {
    return this.totalWaves;
  }

  getWaveStats(): WaveStats {
    return { ...this.waveStats };
  }

  getRemainingMonsters(): number {
    const resolved = this.waveStats.killedCount + this.waveStats.leakedCount;
    return this.waveStats.spawnedCount - resolved;
  }

  isWaveActive(): boolean {
    return this.state === 'spawning' || this.state === 'waiting';
  }

  isGameOver(): boolean {
    return this.state === 'victory' || this.state === 'defeat';
  }

  isVictory(): boolean {
    return this.state === 'victory';
  }

  getActiveBoss(): Boss | null {
    return this.activeBoss;
  }

  isBossActive(): boolean {
    return this.activeBoss !== null && !this.activeBoss.isDead();
  }

  /**
   * Calculate estimated duration for current wave to complete
   */
  private calculateWaveDuration(): number {
    // Base duration on spawn tasks
    let maxSpawnTime = 0;
    for (const task of this.spawnTasks) {
      const taskDuration = task.interval * task.count + task.delay;
      maxSpawnTime = Math.max(maxSpawnTime, taskDuration);
    }

    // Add time for monsters to travel the path (rough estimate ~20s for slow, ~10s for fast)
    const travelTime = 15;

    // Add inter-wave delay
    return maxSpawnTime + travelTime + this.waveEndDelay + this.waveStartDelay;
  }

  /**
   * Get preview data for the next wave (for WavePreviewUI)
   */
  getNextWavePreview(): {
    waveIndex: number;
    primaryMonsterId: string;
    monsterTypes: string[];
    isBoss: boolean;
    bossType?: string;
    startInSeconds: number;
    totalCountdown: number;
  } | null {
    // If we've completed all waves, no preview
    if (this.currentWave >= this.totalWaves) return null;
    if (this.state === 'victory' || this.state === 'defeat') return null;

    const nextWaveNumber = this.currentWave + 1;
    const waveData = LevelLoader.getWaveData(this.scenarioId, nextWaveNumber);
    if (!waveData) return null;

    // Get monster types for this wave
    const monsterTypes = waveData.spawns.map(s => s.monsterType);
    const primaryMonsterId = monsterTypes[0] || 'crawler';

    // Check for boss
    const isBoss = !!waveData.boss;
    const bossType = waveData.boss?.type;

    // Calculate time remaining until next wave's first monster
    let startInSeconds = 0;
    let totalCountdown = this.estimatedWaveDuration;

    if (this.state === 'idle') {
      // Before first wave
      startInSeconds = Math.max(0, this.waveStartDelay - this.stateTimer);
      totalCountdown = this.waveStartDelay;
    } else if (this.state === 'complete') {
      // Between waves
      startInSeconds = Math.max(0, this.waveEndDelay - this.stateTimer);
      totalCountdown = this.waveEndDelay;
    } else if (this.state === 'spawning' || this.state === 'waiting') {
      // Mid-wave - show countdown to next wave based on elapsed time
      const elapsedMs = Date.now() - this.waveSpawnStartTime;
      const elapsedSeconds = elapsedMs / 1000;
      startInSeconds = Math.max(0, this.estimatedWaveDuration - elapsedSeconds);
      totalCountdown = this.estimatedWaveDuration;
    } else if (this.state === 'starting') {
      // Brief transition - show full countdown
      startInSeconds = this.estimatedWaveDuration;
      totalCountdown = this.estimatedWaveDuration;
    }

    return {
      waveIndex: nextWaveNumber,
      primaryMonsterId,
      monsterTypes,
      isBoss,
      bossType,
      startInSeconds,
      totalCountdown,
    };
  }

  /**
   * Get time until next wave starts (for progress bar)
   */
  getTimeUntilNextWave(): number {
    if (this.state === 'idle') {
      return Math.max(0, this.waveStartDelay - this.stateTimer);
    } else if (this.state === 'complete') {
      return Math.max(0, this.waveEndDelay - this.stateTimer);
    }
    return -1; // Mid-wave
  }

  // ============================================
  // Configuration
  // ============================================

  setAutoStart(auto: boolean): void {
    this.autoStart = auto;
  }

  setWaveStartDelay(seconds: number): void {
    this.waveStartDelay = seconds;
  }

  setWaveEndDelay(seconds: number): void {
    this.waveEndDelay = seconds;
  }

  /**
   * Set spawn interval multiplier (default 1.5). Lower = monsters spawn closer together.
   * Intervals are always clamped to MIN_SPAWN_INTERVAL_BY_TYPE so sprites don't overlap.
   */
  setSpawnIntervalMultiplier(multiplier: number): void {
    this.spawnIntervalMultiplier = multiplier;
  }

  /**
   * Scale difficulty by player count (1–4). Intervals and wave countdowns stay the same as 1P.
   * Monster count increases by 30% per additional player (2P=1.3x, 3P=1.6x, 4P=1.9x).
   */
  setDifficultyByPlayerCount(playerCount: number): void {
    if (playerCount <= 1) return;

    // Intervals and wave countdowns: same for 2P, 3P, 4P as 1P (no change)
    this.waveEndDelay = 2;
    this.waveStartDelay = 2;
    this.spawnIntervalMultiplier = 1.5;

    // +30% more monsters per additional player
    this.monsterCountMultiplier = 1 + (Math.min(playerCount, 4) - 1) * 0.3;

    console.log(`[WaveSpawner] ${playerCount}P: same intervals/countdowns, monster count ${this.monsterCountMultiplier}x`);
  }

  // ============================================
  // Cleanup
  // ============================================

  reset(): void {
    this.state = 'idle';
    this.currentWave = 0;
    this.spawnTasks = [];
    this.bossTask = null;
    this.activeBoss = null;
    this.waveStats = this.createEmptyStats(0);
    this.stateTimer = 0;
  }

  destroy(): void {
    this.reset();
    this.scene = null;
  }
}

// Singleton instance
export const WaveSpawner = new WaveSpawnerManager();
